import { TListResponse } from 'src/common/types';
import { TTableSort, TTableSortCols } from 'src/components/table/thead/col';

import { adaptProviderSourceFromResponse, TProviderSource, TProviderSourceResponse } from './provider-source.type';
import { adaptBrandSourceFromResponse, TBrandSource, TBrandSourceResponse } from './brand-source.type';


export const ProductFullListSortCol = {
	NAME: 'name',
	SERIAL_NUMBER: 'serialNumber',
	BRAND: 'brand',
	PROVIDER: 'provider',
	PRICE: 'price',
	AMOUNT: 'amount',
	STORAGE_NAME: 'storageName',
};

export type TProductFullListSortCol = TTableSortCols<typeof ProductFullListSortCol>;

export type TProductFullListQuery = {
	limit: number;
	offset: number;
	search?: string;
	searchBrandName?: string;
	searchSerialNumber?: string;
	searchProvider?: string;
	searchName?: string;
	searchStorageName?: string;
	sort?: TTableSort;
	sortCol?: TProductFullListSortCol;
	isAnalogues?: boolean;
	brandId?: number;
	userNickname?: string;
	serialNumber?: string;
};

export type TProductFullListQueryRequest = {
	limit?: number;
	offset?: number;
	search?: string;
	sort?: string;
	sort_col?: string;
	is_analogues?: boolean;
	brand_id?: number;
	user_nickname?: string;
	serial_number?: string;
};

export function adaptProductFullListQueryToRequest(data?: TProductFullListQuery): TProductFullListQueryRequest {
	if (!data) {
		return {};
	}

	const {
		sortCol,
		...restFields
	} = data;

	return {
		sort_col: sortCol,
		...restFields
	};
}

export type TProductFull = {
  id: string;
  originalId: string;
  serialNumber: string;
  name: string;
  storageId: string;
  storageName: string;
  amount: number;
  amountType: string;
  amountMultiplicity: number;
  price: number;
  isReturnable: boolean;
  isReturnableText: string;
  provider: TProviderSource;
  brand: TBrandSource;
};

export type TProductFullResponse = {
  id: string | number;
  serial_number: string;
  name: string;
  storage_id: string;
  storage_name: string;
  amount: number;
  amount_type: string;
  amount_multiplicity: number;
  price: number;
  is_returnable?: boolean;
	provider: TProviderSourceResponse;
  brand: TBrandSourceResponse;
};

export type TProductFullListResponse = TListResponse<TProductFullResponse>;

export type TProductFullList = TListResponse<TProductFull>;

export type TProductFullPayloadRequest = {
	userNickname: string;
	serialNumber: string;
	brandId?: number;
	isAnalogues: boolean;
};

export type TProductFullRequest = {
	user_nickname: string;
	serial_number: string;
	brand_id?: number;
	is_analogues: boolean;
};

export type TProductFullHistoryItem = TProductFullPayloadRequest & {
	date: string;
	key: string;
};

export async function adaptProductFullFromListResponse(data: TProductFullListResponse): Promise<TProductFullList> {
	const {
		items: itemsRaw = [],
		...restFields
	} = data;

	const items = itemsRaw.map((item, index) => adaptProductFullFromResponse(item, index));

	return {
		items,
		...restFields
	};
}

export function adaptProductFullFromResponse(data: TProductFullResponse, index?: number): TProductFull {
	const {
		id: originalIdRaw,
		serial_number: serialNumber,
		name = '',
		storage_id: storageId,
		storage_name: storageName,
		amount,
		amount_type: amountType,
		amount_multiplicity: amountMultiplicity,
		price,
		is_returnable: isReturnable = false,
		provider: providerRaw,
		brand: brandRaw,
	} = data;

	const provider = adaptProviderSourceFromResponse(providerRaw);
	const brand = adaptBrandSourceFromResponse(brandRaw);

	const originalId = encodeURIComponent(`${originalIdRaw}`);
	const id = `${originalId}-${storageId}-${amount}-${index}`;
	const isReturnableText = isReturnable ? 'Да' : 'Нет';

	return {
		originalId,
		id,
		serialNumber,
		name,
		storageId,
		storageName,
		amount,
		amountType,
		amountMultiplicity,
		price,
		isReturnable,
		isReturnableText,
		provider,
		brand,
	};
}

export function adaptProductFullToRequest(data: TProductFullPayloadRequest): TProductFullRequest {
	const {
		userNickname,
		serialNumber,
		brandId,
		isAnalogues,
	} = data;

	return {
		user_nickname: userNickname,
		serial_number: serialNumber,
		brand_id: brandId,
		is_analogues: isAnalogues,
	};
}
